/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import deepmerge from 'deepmerge';
import { Lang, translate as _translate } from '@adobe-commerce/elsie/i18n';
import { config } from '@/api';
import defaultTranslations from '@/i18n/en_US.json';
import { DEFAULT_LOCALE } from '@/hooks/useLocale';
import { events } from '@adobe-commerce/event-bus';

type DeepStringTree = string | { [k: string]: DeepStringTree };

/**
 * Translates a text into the current locale (imperative version of useText hook).
 *
 * Example
 * ~~~
 *
 *    translate("PaymentServices.messages.methodLoading")
 *
 *  @param key key of text to translate
 */
export function translate(key: string): string {
  const locale = events.lastPayload('locale') || DEFAULT_LOCALE;
  const translations = getDefinitionByLanguage(locale);
  return _translate(key, '', translations);
}

function getDefinitionByLanguage(lang: Lang): DeepStringTree {
  const langDefinitions = config.getConfig().langDefinitions;
  if (!langDefinitions) {
    return defaultTranslations;
  }

  const translations = deepmerge.all([
    defaultTranslations,
    langDefinitions.default || {},
    langDefinitions[lang] || {},
  ]);

  return translations as DeepStringTree;
}
